import { clsx } from "clsx";

import { type Allowance } from "~/api/products/types";

import { ProductTagsWrapper } from "../product-tags/product-tags";
import { ProductImage } from "../ui/product-image/product-image";
import { StampcardCollectBadge } from "../ui/stampcard-collect-badge/stampcard-collect-badge";

import { AllowanceProductBadge } from "./allowance-product-badge";
import { StampcardRewardBadge } from "./stampcard-reward-badge";

type ProductCardProps = {
  id: string;
  name: string;
  displayPrice?: string;
  displayPriceOriginal?: string;
  allowance?: Allowance | null;
  tags: string[];
  image: string | null;
  stamps: number | null;
  stampcardRuleId?: string;
  disabled?: boolean;
  badge?: React.ReactNode;
  quantityControl?: React.ReactNode;
  onItemClick: (productId: string) => void;
};

export const ProductCard = ({
  id,
  name,
  displayPrice,
  displayPriceOriginal,
  allowance,
  tags,
  image,
  stamps,
  stampcardRuleId,
  disabled = false,
  badge,
  quantityControl,
  onItemClick
}: ProductCardProps) => {
  return (
    <div className="relative w-full">
      <button
        className="flex w-full flex-col gap-4"
        type="button"
        onClick={() => {
          onItemClick(id);
        }}
        disabled={disabled}
      >
        {badge ? (
          <div className="pointer-events-none absolute -right-2 -top-6 z-30">
            {badge}
          </div>
        ) : null}

        <ProductImage
          image={image}
          alt={name}
          className="h-56"
          disabled={disabled}
          height="350" // with buffer for larger displays
          quality="50"
        >
          <div className="flex h-full flex-col-reverse justify-between p-3">
            <ProductTagsWrapper
              tags={tags}
              languageCode="en"
              showLabel={false}
            />

            {stamps && !stampcardRuleId ? (
              <StampcardCollectBadge count={stamps} />
            ) : null}

            {stampcardRuleId ? <StampcardRewardBadge /> : null}
          </div>
        </ProductImage>

        <div
          className={clsx("flex flex-col gap-2 text-start text-2xl", {
            "opacity-50": disabled
          })}
        >
          <p className="line-clamp-2 w-full font-semibold text-kiosk-text-primary">
            {name}
          </p>

          {displayPrice ? (
            <div className="flex items-center gap-3">
              {allowance && !allowance.isAllowanceOver ? (
                <AllowanceProductBadge price={displayPrice} />
              ) : (
                <p className="font-bold text-kiosk-text-primary">
                  {displayPrice}
                </p>
              )}

              {displayPriceOriginal ? (
                <p className="text-kiosk-text-secondary line-through">
                  {displayPriceOriginal}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </button>

      {quantityControl ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-3">
          {quantityControl}
        </div>
      ) : null}
    </div>
  );
};
